import React, { useMemo } from 'react'
import cx from 'classnames'
import { observer } from 'mobx-react'
import styles from './WorkpapersForm.sass'
import Spinner from 'components/Spinner'
import AnimatedIcon from 'components/AnimatedIcon'
import WorkpapersFormViewModel from './WorkpapersFormViewModel'
import {
  CheckboxInputContainer,
  DateInputContainer,
  Form,
  FormRow,
  NumericInputContainer,
  TextInputContainer,
} from 'components/inputs'
import { DocumentField } from 'documents/Document'
import { DocumentTypeField } from 'documents/DocumentType'

interface WorkpapersFormProps {
  vm: WorkpapersFormViewModel
}

export default observer(function WorkpapersForm({ vm }: WorkpapersFormProps) {
  return (
    <div className={cx(styles.contentFieldset)}>
      {vm.isPending ? (
        <PendingExtractionView />
      ) : (
        <CorrectWorkpapersForm vm={vm} />
      )}
    </div>
  )
})

const PendingExtractionView = observer(function ProgressView() {
  const { blue400 } = useMemo(() => {
    const bodyStyles = getComputedStyle(document.body)
    const blue400 = bodyStyles.getPropertyValue('--blue400')

    return { blue400 }
  }, [])

  return (
    <div className={cx(styles.contentFieldsetLoader)}>
      <div className={cx(styles.contentFieldsetLoaderIcon)}>
        <div className={styles.contentFieldsetLoaderInnerIcon}>
          <AnimatedIcon name="ic-taxfyle-loop" size={40} />
        </div>

        <Spinner thickness={4} color={blue400} size={80} />
      </div>

      <div className={cx(styles.contentFieldsetTitle, styles.centered)}>
        OCR is processing
        <span className={cx(styles.contentFieldsetHelp)}>
          Please check back again in a few minutes.
        </span>
      </div>
    </div>
  )
})

const CorrectWorkpapersForm = observer(function FormView({
  vm,
}: WorkpapersFormProps) {
  const hasWorpaperNodes = vm.workpaperNodes.length > 0

  return (
    <>
      <div className={cx(styles.contentFieldsetTitle)}>
        <span>
          {vm.documentState}&nbsp;{vm.documentType}
        </span>

        {hasWorpaperNodes && (
          <span className={cx(styles.contentFieldsetHelp)}>
            Compare, review and update all captured data from the shared file,{' '}
            <strong>{vm.documentTitle}</strong>
          </span>
        )}

        {!hasWorpaperNodes && (
          <span
            className={cx(styles.contentFieldsetHelp)}
            data-test="workpaper-fields-empty"
          >
            Unable to capture any data from the shared file,{' '}
            <strong>{vm.documentTitle}</strong>
          </span>
        )}
      </div>

      {hasWorpaperNodes && (
        <Form>
          {vm.workpaperNodes.map((node, index) => {
            if (typeof node === 'string') {
              return (
                <div key={index} className={styles.contentFieldsetFormTitle}>
                  {node}
                </div>
              )
            }

            return (
              <div key={index} className={styles.contentFieldsetForm}>
                {node.field ? (
                  <WorkpaperFormField field={node.field} type={node.type} />
                ) : (
                  <div className={styles.contentFieldsetFormSubTitle}>
                    {node.type.label} {node.position}
                  </div>
                )}
              </div>
            )
          })}
        </Form>
      )}
    </>
  )
})

interface WorkpaperFormFieldProps {
  field: DocumentField
  type: DocumentTypeField
}

const WorkpaperFormField = observer(function WorkpaperFormField({
  field,
  type,
}: WorkpaperFormFieldProps) {
  const label = `${type.label} ${type.required ? '*' : ''}`

  switch (field.type) {
    case 'TEXT':
      return (
        <FormRow>
          <TextInputContainer
            model={field}
            field="value"
            name={field.id}
            label={label}
            help={type.description}
            data-test={`workpaper-field-${field.id}`}
          />
        </FormRow>
      )
    case 'NUMERIC':
      return (
        <FormRow>
          <NumericInputContainer
            model={field}
            field="value"
            label={label}
            help={type.description}
            precision={field.isInteger ? undefined : 2}
            data-test={`workpaper-field-${field.id}`}
          />
        </FormRow>
      )
    case 'BOOL':
      return (
        <FormRow>
          <CheckboxInputContainer
            model={field}
            field="value"
            label={label}
            help={type.description}
            data-test={`workpaper-field-${field.id}`}
          />
        </FormRow>
      )
    case 'DATE':
      return (
        <FormRow>
          <DateInputContainer
            model={field}
            field="value"
            label={label}
            help={type.description}
            dataTest={`workpaper-field-${field.id}`}
          />
        </FormRow>
      )
    default:
      throw new Error('Unsupported workpaper field type.')
  }
})
